/**
 * Onboarding Component
 * Main orchestrator for the onboarding experience
 *
 * NOTE: This component handles tooltip-based tours AFTER the setup wizard completes.
 * The SetupWizard is shown for first-time users via SetupWizardGuard on Dashboard.
 * Once setup wizard is complete, this component shows feature tours and checklists.
 */

import React, { useEffect, useState } from 'react';
import { useOnboardingContext } from './OnboardingProvider';
import { OnboardingModal } from './OnboardingModal';
import { OnboardingTooltip } from './OnboardingTooltip';
import { OnboardingChecklist } from './OnboardingChecklist';
import { useAuth } from '@/components/auth/AuthProvider';
import { useProfile } from '@/hooks/useProfile';

/**
 * Onboarding Component
 * Manages the display of welcome modal, tour tooltips, and checklist
 * Only renders for authenticated users who have completed the setup wizard
 */
export const Onboarding: React.FC = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile(user?.id || '');
  const {
    progress,
    currentTour,
    currentStepIndex,
    isActive,
    nextStep,
    previousStep,
    skipTour,
    shouldShowOnboarding,
  } = useOnboardingContext();

  // State must be declared before any conditional returns
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Check if user has completed setup wizard
  const hasCompletedSetup = profile?.hasCompletedSetup ?? false;

  /**
   * Show welcome modal for users who completed setup but haven't seen the tours
   * The welcome modal introduces the feature tours after setup wizard
   */
  useEffect(() => {
    // Only show if:
    // - Authenticated
    // - Has completed setup wizard
    // - Should show onboarding (tour not completed/skipped)
    // - Hasn't seen welcome modal yet
    if (!authLoading && !profileLoading && isAuthenticated && hasCompletedSetup &&
        shouldShowOnboarding() && !progress.hasSeenWelcome) {
      // Delay to ensure the app is fully loaded
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [authLoading, profileLoading, isAuthenticated, hasCompletedSetup, shouldShowOnboarding, progress.hasSeenWelcome]);

  // Don't show onboarding until we know the user is authenticated and has completed setup
  if (authLoading || profileLoading || !isAuthenticated || !hasCompletedSetup) {
    return null;
  }

  /**
   * Get current step
   */
  const currentStep = currentTour?.steps[currentStepIndex];

  /**
   * Show checklist if onboarding is in progress
   */
  const showChecklist =
    shouldShowOnboarding() || progress.completedChecklistItems.length > 0;

  return (
    <>
      {/* Welcome Modal */}
      <OnboardingModal open={showWelcomeModal} onOpenChange={setShowWelcomeModal} />

      {/* Active Tour Tooltip */}
      {isActive && currentStep && currentTour && (
        <OnboardingTooltip
          step={currentStep}
          stepNumber={currentStepIndex + 1}
          totalSteps={currentTour.steps.length}
          onNext={nextStep}
          onPrevious={currentStepIndex > 0 ? previousStep : undefined}
          onSkip={skipTour}
        />
      )}

      {/* Onboarding Checklist */}
      {showChecklist && !progress.onboardingComplete && (
        <OnboardingChecklist position="floating" />
      )}
    </>
  );
};
